import { useState } from 'react'
import { redirect, useLoaderData, useNavigate } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '@/components/subscription/button'
import { TextLink } from '@/components/subscription/text-link'
import { ScheduleSelector } from '@/components/subscription/checkout/schedule-selector'
import { formatDeliverAt } from '@/lib/shipment'
import { createClient, JSONAPIResponse } from '@/api-client'
import { ContentType } from '@/api-client/http-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { SubscriptionInventory } from '@/api-client/data-contracts'
import * as styles from './styles.css'

export const loader = async () => {
  const subscriptions = (await fetchSubscriptions()) as unknown as JSONAPIResponse<typeof fetchSubscriptions>
  const subscription = subscriptions.data.data[0]
  if (subscription) {
    const address = (await fetchAddress(subscription)) as unknown as JSONAPIResponse<typeof fetchAddress>
    const schedules = await fetchSchedules(address.data.data.attributes.postal_code)
    const placements = await fetchPlacements(subscription.id)

    return [subscriptions, schedules, placements]
  } else {
    return redirect('/subscription/mypage')
  }
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions()
}

function fetchAddress(subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(subscription.id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, upcoming: '1' })
}

function fetchPlacements(id) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

export const SubscriptionMypageSchedule: React.FC = () => {
  const [
    {
      data: { data: subscriptions, included: subscriptionsIncluded },
    },
    {
      data: { data: schedules, included: schedulesIncluded },
    },
    {
      data: { data: placements },
    },
  ] = useLoaderData() as [JSONAPIResponse<typeof fetchSubscriptions>, JSONAPIResponse<typeof fetchSchedules>, JSONAPIResponse<typeof fetchPlacements>]
  const navigate = useNavigate()
  const subscription = subscriptions[0]
  const inventories = schedulesIncluded.filter(
    (item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout
  ) as unknown as SubscriptionInventory[]
  const [selectedInventories, setSelectedInventories] = useState(
    subscriptionsIncluded.filter((item) => item.type === 'subscription_inventory') as unknown as SubscriptionInventory[]
  )

  let options = []
  if (inventories[0]) options.push([inventories[0]])
  if (inventories.length > 2) options.push([inventories[0], inventories[2]])
  if (inventories.length > 3) options.push(inventories)

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async () => {
    try {
      await createClient(Subscriptions).putSubscriptionsSubscriptionIdContract(subscription.id, { inventory_ids: selectedInventories.map((item) => item.id) }, { type: ContentType.Json })
      await navigate('/subscription/mypage', { state: { message: 'お届けスケジュールを変更しました' } })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>受け取りスケジュール変更</PageHeading>
      <Flex direction="column" align="center" gap="24px" width="100%">
        <ScheduleSelector
          schedules={schedules}
          selectedInventories={selectedInventories}
          onChange={(ids) => setSelectedInventories(inventories.filter((item) => ids.includes(item.id)))}
        />
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <h3 className={styles.cardHeading}>変更後のお届け予定</h3>
          <Flex direction="column" align="start" gap="8px">
            {selectedInventories.map((inventory) => {
              const schedule = schedules.find((s) => s.relationships.inventory.data.id === inventory.id)
              const date = schedule.attributes.deliver_at.map((ts) => formatDeliverAt(1000 * ts)).join('〜')

              return (
                <span key={inventory.id} className={styles.scheduledDate}>
                  {date}
                </span>
              )
            })}
          </Flex>
          {/* TODO: 次回のplacement.status === orderedでかつ曜日を変えようとしたときに表示。 */}
          {/* <p className={styles.warn}>※すでに次回分は出荷準備中のため、それ以降変更が適用されます</p> */}
        </Flex>
        <Flex direction="column" align="center" gap="16px" width="100%">
          <Button fluid as="button" onClick={handleSubmit}>
            保存
          </Button>
          <TextLink to="/subscription/mypage">マイページに戻る</TextLink>
        </Flex>
      </Flex>
    </Flex>
  )
}
